/** AI Studio — types + API bindings. */
import { http } from "@/shared/api/http";

export interface CreativeBriefIn {
  objective: string;
  audience?: string | null;
  offer?: string | null;
  call_to_action?: string | null;
  campaign?: string | null;
  platforms?: string[];
  tone?: string | null;
  length?: string | null;
  notes?: string | null;
  creativity?: string;
}

export interface GeneratedAsset {
  id: string;
  workspace_id: string;
  campaign_pack_id: string | null;
  content_type: string;
  category: string;
  capability: string;
  body_markdown: string | null;
  body_json: Record<string, unknown> | null;
  image_prompt: string | null;
  provider: string;
  model: string;
  prompt_key: string;
  prompt_version: string;
  brand_brain_version: string | null;
  prompt_tokens: number;
  completion_tokens: number;
  estimated_cost_usd: number;
  latency_ms: number;
  status: "draft" | "needs_review";
  error: string | null;
  regenerated_from: string | null;
  campaign: string | null;
  objective: string | null;
  created_at: string;
  /** Canonical asset id — stable across revisions. Introduced Patch 28. */
  permanent_asset_id?: string | null;
  /** Current revision number for the permanent asset. Defaults to 1. */
  version_number?: number;
  /** Whether this row represents the current head of the permanent asset. */
  is_head?: boolean;
  /** Optional link to the creative brief that produced this asset. */
  creative_brief_id?: string | null;
  /** Patch 35.1b — files attached to this asset (rendered media). */
  media_file_ids?: string[];
  /** Patch 35.1b — write-time maturity classifier. */
  payload_maturity?: "specification" | "rendered_deliverable" | null;
  /** Patch 35.1b — this row is no longer the head of its permanent id. */
  superseded?: boolean;
  /** Patch 35.1b — chain pointer to the previous version's row. */
  parent_asset_id?: string | null;
  /** Patch 37.4.44 — truthful degraded-row flag. When ``true``, the
   *  raw Mongo document could not be reconstructed via the domain
   *  model and this row is rendered as a "needs recovery" placeholder.
   *  Prompts, provider names, model ids, tokens, cost, latency, and
   *  media file ids are ``null`` on a placeholder. */
  needs_recovery?: boolean;
}

/** Patch 35.1b — Signed short-lived URL for one media file on an asset. */
export interface SignedMediaUrl {
  url: string;
  ttl_seconds: number;
  mime_type: string;
  width: number | null;
  height: number | null;
  kind: string;
}

/** Patch 35.1b — Media engine render response (preview block). */
export interface MediaEngineRenderPreview {
  new_asset_id: string;
  permanent_asset_id: string;
  version_number: number;
  content_type: string;
  mime_type: string;
  width: number | null;
  height: number | null;
  image_url: string;
}

/** Patch 35.1b — Full media engine render result. ``internal`` is null
 * unless the caller is a workspace admin/owner. */
export interface MediaEngineRenderResult {
  preview: MediaEngineRenderPreview;
  internal: Record<string, unknown> | null;
}

export interface CampaignPack {
  id: string;
  workspace_id: string;
  objective: string;
  campaign_name: string | null;
  preset: string | null;
  content_types: string[];
  asset_ids: string[];
  status:
    | "generating"
    | "draft"
    | "needs_review"
    | "approved"
    | "scheduled"
    | "published"
    | "archived"
    | "partial"
    | "failed";
  total_assets: number;
  completed_assets: number;
  failed_assets: number;
  total_cost_usd: number;
  brand_brain_version: string | null;
  pipeline_stages: string[];
  created_at: string;
  /** Patch 37.4.42 — live pack ownership counts derived from
   *  ai_assets. Prefer these over ``total_assets``/``completed_assets``
   *  which are historical write-time counters.
   *  Optional to keep older backends compatible. */
  live_total?: number | null;
  live_by_status?: Record<string, number> | null;
  is_quick_pack?: boolean;
  is_exportable_playbook?: boolean;
  /** Patch 37.4.43 — server-resolved campaign name (single-source-of-
   *  truth ladder from pack_stats_service). Clients render verbatim
   *  so AI Studio, Content Ops, and Library never drift. */
  campaign_name_resolved?: string | null;
  /** Patch 37.4.44 — canonical breakdown fields.
   *  ``live_hidden`` = head rows whose payload could not be loaded
   *  (schema drift). Rendered to the customer as "N item(s) need
   *  recovery" — never as "legacy row hidden" or "repair schema".
   *  ``live_approved_vaulted`` / ``live_approved_unvaulted`` split
   *  ``live_by_status.approved`` so the UI can honestly render
   *  "Approved N (M in Vault)". */
  live_hidden?: number | null;
  live_approved_vaulted?: number | null;
  live_approved_unvaulted?: number | null;
  /** Patch 37.4.52 — canonical campaign provenance. Standalone Studio
   * packs intentionally have no blueprint and never fabricate one. */
  blueprint_id?: string | null;
  pack_origin?: "campaign" | "ai_studio" | "ai_studio_ad_hoc";
  campaign_room_path?: string | null;
}

export interface ContentTypeInfo {
  key: string;
  label: string;
  category: string;
  capability: string;
  output_format: "markdown" | "plaintext" | "json";
  default_length: string;
  is_bundle_default: boolean;
}

export interface StarterTemplate {
  key: string;
  label: string;
  description: string;
  icon: string;
  objective: string;
  audience?: string | null;
  offer?: string | null;
  call_to_action?: string | null;
  campaign?: string | null;
  platforms?: string[];
  tone?: string | null;
  notes?: string | null;
  suggested_preset: string;
}

export interface PipelineStage {
  key: string;
  label: string;
  description: string;
}

export interface SavedBrief extends CreativeBriefIn {
  id: string;
  workspace_id: string;
  name: string;
  times_used: number;
  created_at: string;
  updated_at: string;
}

export const listTemplates = (): Promise<StarterTemplate[]> => http("/ai-studio/templates");
export const listContentTypes = (): Promise<ContentTypeInfo[]> => http("/ai-studio/content-types");
export const listPipelineStages = (): Promise<PipelineStage[]> => http("/ai-studio/pipeline");

export const generateSingle = (
  workspaceId: string,
  payload: { content_type: string; brief: CreativeBriefIn; preference?: { primary?: string; fallback?: string }; model_override?: string },
): Promise<GeneratedAsset> =>
  http(`/workspaces/${workspaceId}/ai-studio/generate`, { method: "POST", body: payload });

export const regenerateAsset = (workspaceId: string, assetId: string): Promise<GeneratedAsset> =>
  http(`/workspaces/${workspaceId}/ai-studio/assets/${assetId}/regenerate`, { method: "POST" });

// Patch 35.1b — Media Engine surface.
export const fetchSignedMediaUrl = (
  workspaceId: string,
  assetId: string,
): Promise<SignedMediaUrl> =>
  http(`/workspaces/${workspaceId}/media-engine/assets/${assetId}/signed-url`);

export const renderImageAsset = (
  workspaceId: string,
  assetId: string,
): Promise<MediaEngineRenderResult> =>
  http(`/workspaces/${workspaceId}/media-engine/assets/${assetId}/render-image`, {
    method: "POST",
  });

// ── Patch 35.2b-i / 35.2b-ii — Video Render Bridge ─────────────────
export interface VideoPreflightHeroRisk {
  has_hero: boolean;
  hero_asset_id?: string | null;
  hero_asset_version?: number | null;
  quality_level?: "strong" | "acceptable" | "risky" | "blocked" | null;
  safe_reason_code?: string | null;
  safe_summary?: string | null;
  warnings?: string[];
  has_accepted_risk?: boolean;
  needs_risk_acceptance?: boolean;
  score?: number | null;
}

export interface VideoPreflight {
  asset_id: string;
  eligible: boolean;
  safe_reason_code?: string | null;
  safe_reason_detail?: string | null;
  recommended_provider_service?: string | null;
  estimated_duration?: number | null;
  estimated_aspect_ratio?: string | null;
  estimated_completion_window?: string | null;
  projected_internal_cost_usd?: number | null;
  selected_capability_internal?: string | null;
  // Patch 37.4.25 — Hero risk surface for the render-confirm UI.
  hero_risk?: VideoPreflightHeroRisk;
}

export interface VideoRenderSubmit {
  job_id: string;
  asset_id: string;
  status: string;
  safe_message: string;
  estimated_completion_window: string;
}

export interface VideoJobStatus {
  job_id: string;
  asset_id: string;
  status: string;                 // safe status (queued/preparing/rendering/finalizing/ready/failed/needs_attention/cancelled)
  progress_stage: string;
  safe_message: string;
  estimated_completion_window: string;
  created_at?: string | null;
  updated_at?: string | null;
  completed_asset_id?: string | null;
  internal?: {
    provider?: string | null;
    provider_job_id?: string | null;
    attempt_count?: number;
    last_polled_at?: string | null;
    cost_usd_estimate?: number | null;
    provider_error?: string | null;
    capability_internal?: string | null;
  } | null;
}

// ── Patch 35.3a — Founder Lifecycle Completion ─────────────────────
export interface AssetLifecycle {
  lifecycle_stage: string;
  primary_action: string | null;
  available_actions: string[];
  // Patch 35.3a.1 — truthful vault_status reflecting real evidence
  // (``vault_registered_at``).
  vault_status:
    | "not_eligible"
    | "eligible_not_registered"
    | "registered";
  next_step_message: string;
}

export interface PackSummary {
  assets_total: number;
  awaiting_review: number;
  approved_text_assets: number;
  image_prompts_ready_to_render: number;
  video_kits_needing_hero: number;
  video_kits_ready_to_render: number;
  rendered_assets_awaiting_final_approval: number;
  approved_not_vaulted: number;
  vaulted_assets: number;
  scheduled_assets: number;
  published_assets: number;
  rendering_in_progress: number;
  archived_assets: number;
  recommended_next_action: string;
  recommended_next_message: string;
  /** Patch 35.3b — executable CTA metadata. */
  recommended_next_label?: string;
  recommended_next_eligible_count?: number;
  recommended_next_asset_ids?: string[];
  /** Patch 35.3b — canonical campaign completion checklist. */
  progress?: PackProgress;
  current_stage?: string | null;
  last_completed_stage?: string | null;
}

/** Patch 35.3b — Campaign completion checklist stage. */
export type StageState = "complete" | "active" | "not_started" | "blocked";

export interface StageEntry {
  state: StageState;
  complete?: number;
  total?: number;
}

export interface PackProgress {
  campaign_setup: StageEntry;
  strategy_generated: StageEntry;
  copy_reviewed: StageEntry;
  image_prompts_reviewed: StageEntry;
  images_rendered: StageEntry;
  final_media_approved: StageEntry;
  vaulted: StageEntry;
  scheduled: StageEntry;
  published: StageEntry;
}

// ── Patch 35.3b — Batch image render bindings ─────────────────────
export interface BatchPreflightAsset {
  asset_id: string;
  eligible: boolean;
  blocker: string | null;
  blocker_detail: string | null;
  content_type: string | null;
  current_status: string | null;
  version_number: number | null;
}

export interface BatchPreflightOut {
  pack_id: string;
  pack_name: string | null;
  requested_count: number;
  eligible_count: number;
  blocked_count: number;
  estimated_provider_calls: number;
  max_batch_size: number;
  budget_ok: boolean;
  projected_cost_usd: number | null;
  avg_cost_per_asset_usd: number | null;
  budget_warning: boolean | null;
  spent_today_usd: number | null;
  assets: BatchPreflightAsset[];
}

export interface BatchJobOut {
  asset_id: string;
  state: "completed" | "failed" | "blocked" | "already_running";
  new_asset_id: string | null;
  permanent_asset_id: string | null;
  version_number: number | null;
  mime_type: string | null;
  width: number | null;
  height: number | null;
  image_url: string | null;
  blocker: string | null;
  blocker_detail: string | null;
  error_code: string | null;
  provider: string | null;
  model_internal: string | null;
  latency_ms: number | null;
  cost_usd_estimate: number | null;
  retries_used: number | null;
}

export interface BatchExecuteOut {
  pack_id: string;
  batch_id: string;
  totals: Record<string, number>;
  jobs: BatchJobOut[];
}

export const batchRenderImagePreflight = (
  workspaceId: string, packId: string, assetIds: string[],
): Promise<BatchPreflightOut> =>
  http(`/workspaces/${workspaceId}/media-engine/packs/${packId}/batch-render/preflight`, {
    method: "POST",
    body: { asset_ids: assetIds },
  });

export const batchRenderImageExecute = (
  workspaceId: string, packId: string, assetIds: string[],
): Promise<BatchExecuteOut> =>
  http(`/workspaces/${workspaceId}/media-engine/packs/${packId}/batch-render`, {
    method: "POST",
    body: { asset_ids: assetIds },
  });

/** Patch 35.3b — max selectable images per batch, mirrored from backend
 * ``MAX_BATCH_SIZE`` constant. Kept in sync via preflight response. */
export const BATCH_IMAGE_MAX = 3;

export const fetchAssetLifecycle = (
  workspaceId: string, assetId: string,
): Promise<AssetLifecycle> =>
  http(`/workspaces/${workspaceId}/media-engine/assets/${assetId}/lifecycle`);

export const fetchPackSummary = (
  workspaceId: string, packId: string,
): Promise<PackSummary> =>
  http(`/workspaces/${workspaceId}/media-engine/packs/${packId}/summary`);


// ── Patch 35.2c — Hero image guard + candidate picker ─────────────
export interface HeroCandidate {
  asset_id: string;
  media_file_id: string;
  version_number: number;
  campaign_pack_id: string | null;
  same_pack: boolean;
  same_brief: boolean;
  same_campaign: boolean;
  content_type: string | null;
  category: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  width: number | null;
  height: number | null;
  thumbnail_asset_id: string;
}

export interface HeroQualityResult {
  eligible: boolean;
  quality_level: "strong" | "acceptable" | "risky" | "blocked";
  score: number;
  confidence: "high" | "medium" | "low";
  safe_summary: string;
  strengths: string[];
  warnings: string[];
  safe_reason_code?: string | null;
  hero_reference?: {
    hero_asset_id: string;
    hero_media_file_id: string;
    hero_asset_version: number;
    width?: number | null;
    height?: number | null;
    mime_type?: string | null;
  } | null;
}

export const fetchHeroCandidates = (
  workspaceId: string,
  assetId: string,
): Promise<{ candidates: HeroCandidate[]; count: number }> =>
  http(`/workspaces/${workspaceId}/media-engine/assets/${assetId}/hero-candidates`);

export const assessHero = (
  workspaceId: string,
  assetId: string,
  heroAssetId: string,
): Promise<HeroQualityResult> =>
  http(`/workspaces/${workspaceId}/media-engine/assets/${assetId}/hero-assess`, {
    method: "POST",
    body: { hero_asset_id: heroAssetId },
  });

export const selectHero = (
  workspaceId: string,
  assetId: string,
  heroAssetId: string,
): Promise<{ ok: boolean; quality_level: string; score: number }> =>
  http(`/workspaces/${workspaceId}/media-engine/assets/${assetId}/select-hero`, {
    method: "POST",
    body: { hero_asset_id: heroAssetId },
  });

export const videoRenderPreflight = (
  workspaceId: string,
  assetId: string,
): Promise<VideoPreflight> =>
  http(`/workspaces/${workspaceId}/media-engine/assets/${assetId}/render-video/preflight`, {
    method: "POST",
  });

export const videoRenderSubmit = (
  workspaceId: string,
  assetId: string,
  opts?: { risk_override?: string },
): Promise<VideoRenderSubmit> =>
  http(`/workspaces/${workspaceId}/media-engine/assets/${assetId}/render-video`, {
    method: "POST",
    body: opts?.risk_override ? { risk_override: opts.risk_override } : {},
  });

// ── Patch 37.4.25 — Explicit acceptance of a risky hero image.
// Founder-only. Persists a durable acceptance keyed to the currently-
// selected (hero_asset_id, hero_asset_version). Blocked heroes are
// rejected (409, image_blocked/*); safe heroes are rejected as
// hero_not_risky (they don't need acceptance).
export interface AcceptHeroRiskResult {
  ok: boolean;
  quality_level: "risky";
  score: number;
  accepted_at: string;
}

export const acceptHeroRisk = (
  workspaceId: string,
  assetId: string,
  opts: {
    hero_asset_id: string;
    hero_asset_version: number;
    reason?: string;
  },
): Promise<AcceptHeroRiskResult> =>
  http(`/workspaces/${workspaceId}/media-engine/assets/${assetId}/accept-hero-risk`, {
    method: "POST",
    body: opts,
  });

export const videoRenderJobStatus = (
  workspaceId: string,
  jobId: string,
): Promise<VideoJobStatus> =>
  http(`/workspaces/${workspaceId}/media-engine/video-jobs/${jobId}`);

export const videoRenderActiveJob = (
  workspaceId: string,
  assetId: string,
): Promise<VideoJobStatus | null> =>
  http(`/workspaces/${workspaceId}/media-engine/assets/${assetId}/active-video-job`);

// ── Patch 37.4.21 — Regenerate rendered head ────────────────────────
export interface RegenerateRenderedResult {
  preview: {
    new_asset_id: string;
    permanent_asset_id: string;
    version_number: number;
    content_type: string;
    mime_type: string;
    width?: number | null;
    height?: number | null;
    image_url: string;
  };
  internal?: unknown;
}

export const regenerateRenderedImage = (
  workspaceId: string,
  renderedAssetId: string,
  body: { feedback_note?: string; cost_ack: boolean },
): Promise<RegenerateRenderedResult> =>
  http(
    `/workspaces/${workspaceId}/media-engine/rendered-assets/${renderedAssetId}/regenerate`,
    { method: "POST", body },
  );

export const sendAssetToReview = (workspaceId: string, assetId: string): Promise<GeneratedAsset> =>
  http(`/workspaces/${workspaceId}/ai-studio/assets/${assetId}/send-to-review`, { method: "POST" });

// ── Patch 37.4.42 — Idempotent bulk Send-to-Review for a whole pack.
// Per-item results; already-``needs_review`` returns ``skipped_not_draft``
// (idempotent 200); protected states (approved/scheduled/published/
// archived/failed) return ``skipped_locked`` and are never mutated;
// one ineligible item never fails the batch.
export type BulkSendItemStatus =
  | "sent"
  | "skipped_not_draft"
  | "skipped_locked"
  | "not_found"
  | "needs_recovery"
  | "error";

export interface BulkSendToReviewItem {
  asset_id: string;
  permanent_asset_id: string | null;
  content_type: string | null;
  status: BulkSendItemStatus;
  reason: string | null;
}

export interface BulkSendToReviewOut {
  pack_id: string;
  total: number;
  sent: number;
  skipped_not_draft: number;
  skipped_locked: number;
  errors: number;
  /** Patch 37.4.44 — typed blocked outcome for malformed heads. The
   *  bulk operation refuses to send these to review; they must be
   *  recovered first via the founder diagnostic. */
  needs_recovery?: number;
  results: BulkSendToReviewItem[];
}

export const sendPackToReview = (
  workspaceId: string,
  packId: string,
): Promise<BulkSendToReviewOut> =>
  http(`/workspaces/${workspaceId}/campaign-packs/${packId}/send-to-review`, {
    method: "POST",
  });

// Patch 37.4.43 — AI Studio "Recent Generations" panel + pack detail
// route both source their pack list from this endpoint. Same
// wire-shape as ``listPacks``; the alias exists so the founder-facing
// panel and the router-level tests can lock to a semantic URL.
export const listRecentPacks = (
  workspaceId: string,
  limit = 20,
): Promise<CampaignPack[]> =>
  http(`/workspaces/${workspaceId}/ai-studio/recent-packs?limit=${limit}`);

export const editAsset = (workspaceId: string, assetId: string, body_markdown: string): Promise<GeneratedAsset> =>
  http(`/workspaces/${workspaceId}/ai-studio/assets/${assetId}`, { method: "PATCH", body: { body_markdown } });

export const fetchAsset = (workspaceId: string, assetId: string): Promise<GeneratedAsset> =>
  http(`/workspaces/${workspaceId}/ai-studio/assets/${assetId}`);

export const listHistory = (workspaceId: string, limit = 50): Promise<GeneratedAsset[]> =>
  http(`/workspaces/${workspaceId}/ai-studio/history?limit=${limit}`);

export const createPack = (
  workspaceId: string,
  payload: { brief: CreativeBriefIn; preset?: string; content_types?: string[]; preference?: { primary?: string; fallback?: string } },
): Promise<CampaignPack> =>
  http(`/workspaces/${workspaceId}/campaign-packs`, { method: "POST", body: payload });

export const fetchPack = (workspaceId: string, packId: string): Promise<CampaignPack> =>
  http(`/workspaces/${workspaceId}/campaign-packs/${packId}`);

export const fetchPackAssets = (workspaceId: string, packId: string): Promise<GeneratedAsset[]> =>
  http(`/workspaces/${workspaceId}/campaign-packs/${packId}/assets`);

export const listSavedBriefs = (workspaceId: string): Promise<SavedBrief[]> =>
  http(`/workspaces/${workspaceId}/ai-memory/briefs`);

export const saveBrief = (workspaceId: string, name: string, brief: CreativeBriefIn): Promise<SavedBrief> =>
  http(`/workspaces/${workspaceId}/ai-memory/briefs`, { method: "POST", body: { name, brief } });

export const deleteSavedBrief = (workspaceId: string, briefId: string): Promise<void> =>
  http(`/workspaces/${workspaceId}/ai-memory/briefs/${briefId}`, { method: "DELETE" });

export const PACK_PRESETS: { key: string; label: string; hint: string }[] = [
  { key: "full_awareness",  label: "Full Awareness Bundle", hint: "The judge-demo pack — every core channel." },
  { key: "social_only",     label: "Social Only",           hint: "Facebook, Instagram, X, LinkedIn, GBP." },
  { key: "email_and_sms",   label: "Email & SMS",           hint: "Direct-to-inbox / phone." },
  { key: "local_print",     label: "Local Print",           hint: "Flyer + door hanger + graphic prompt." },
  { key: "video_hero",      label: "Video Hero",            hint: "Video Production Kit + supporting posts." },
  { key: "custom",          label: "Custom Selection",      hint: "Pick individual content types below." },
];
